import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";

// Renders the tournament CONTENT (only) at a chosen resolution while the
// real Header/BottomNav around it stay at the device's own real width - see
// Header.js's bottom-nav zoom control (+/- next to the loupe icon, cycling
// through RESOLUTION_PRESETS) for where previewWidth comes from.
//
// Plain CSS (a fixed-width container) can't do this on its own: a lot of
// this app's responsive layout switches on real `@media` breakpoints and on
// useIsWideViewport.js's own `window.innerWidth` reads, both of which only
// ever see the actual browser window - not some inner div's width. The only
// way to get a genuinely different (wider/narrower) layout to render while
// the real window stays whatever size it actually is, is to give that
// content its own real, differently-sized browsing context: a same-origin
// iframe loaded at `previewWidth`, then shrunk to fit back into the space
// available here via a CSS transform (which - unlike resizing the iframe
// itself - doesn't touch anything inside it, so its own `position: fixed`
// elements, media queries and viewport-width reads all still see the real
// `previewWidth`).
//
// The iframe's `src` carries `preview_frame=1` - App.js reads that to skip
// mounting its own Header/Footer/BackToTopButton/ToastContainer (this
// preview only wants Tournament's own content) and to force ITS OWN
// previewWidth to 0 (otherwise, being the same origin, it would read the
// exact same localStorage value straight back out and recurse into another
// nested preview iframe, forever).
const buildPreviewUrl = (pathname, search) => {
    const params = new URLSearchParams(search);
    params.set("preview_frame", "1");
    return { pathname, search: `?${params.toString()}` };
};

const TournamentPreviewFrame = ({ previewWidth }) => {
    const location = useLocation();
    const history = useHistory();
    const wrapperRef = useRef(null);
    const iframeRef = useRef(null);
    const contentObserverRef = useRef(null);
    const [scale, setScale] = useState(1);
    const [contentHeight, setContentHeight] = useState(0);
    const [isLoaded, setIsLoaded] = useState(false);
    const remeasureRef = useRef(null);
    const measureTimerRef = useRef(null);
    const { theme, accent } = useTheme();

    // `src` only changes for a genuinely different tournament (pathname) -
    // that's the only way to make an <iframe> actually (re)navigate.
    // Everything else (tab switches, stage/gridTab params - search-only
    // changes) is soft-navigated via postMessage once loaded instead (see
    // the effect below): changing `src` on every tab click used to reboot
    // the whole framed app from scratch each time (full JS re-parse +
    // refetch), which visibly flashed through whatever tab a fresh boot
    // defaults to (Information) before landing on the one actually
    // requested. App.js's own message listener (isPreviewFrame-gated) is
    // the receiving side - it just calls history.replace(), a normal
    // client-side route change indistinguishable from tapping a tab inside
    // the iframe directly.
    const [src, setSrc] = useState(() => {
        const url = buildPreviewUrl(location.pathname, location.search);
        return `${url.pathname}${url.search}`;
    });
    const pathnameRef = useRef(location.pathname);

    useEffect(() => {
        if (location.pathname !== pathnameRef.current) {
            pathnameRef.current = location.pathname;
            setIsLoaded(false);
            const url = buildPreviewUrl(location.pathname, location.search);
            setSrc(`${url.pathname}${url.search}`);
        }
    }, [location.pathname, location.search]);

    useEffect(() => {
        if (!isLoaded) return;
        const url = buildPreviewUrl(location.pathname, location.search);
        iframeRef.current?.contentWindow?.postMessage(
            { type: "preview-navigate", pathname: url.pathname, search: url.search },
            window.location.origin
        );
        // Re-measure once the framed app has had a moment to actually
        // render the new tab's content - see remeasure's own comment for
        // why the ResizeObserver below can't be trusted to catch this on
        // its own for a switch to SHORTER content.
        const timer = setTimeout(() => remeasureRef.current?.(), 300);
        return () => clearTimeout(timer);
    }, [location.pathname, location.search, isLoaded]);

    // The iframe's own ThemeProvider only reads localStorage once, at its
    // own mount - flipping the theme/accent switch in the real page (this
    // component's own siblings, via the same ThemeContext) afterwards never
    // reaches the already-loaded iframe on its own, since it's a separate
    // document. ThemeContext.js's own preview_frame-gated listener is the
    // receiving side.
    useEffect(() => {
        if (!isLoaded) return;
        iframeRef.current?.contentWindow?.postMessage(
            { type: "preview-theme-changed", theme, accent },
            window.location.origin
        );
    }, [theme, accent, isLoaded]);

    // Reverse direction of the effect above: a stage/place tab tapped
    // *inside* the preview only ever changed the framed copy's own
    // location (its own history.replace calls have no way to reach out
    // here on their own) - the real, outer page never heard about it, so
    // switching back to Mobile/off kept showing whatever was selected
    // *before* the preview was opened (the hidden <Tournament> instance
    // App.js keeps mounted the whole time - see its own comment - never
    // got told anything changed). App.js's own effect (isPreviewFrame-gated)
    // is the sending side. `preview_frame=1` is stripped back out before
    // applying it here - without that, the OUTER page's own URL would gain
    // it and read itself as a preview frame too.
    useEffect(() => {
        const handler = (event) => {
            if (event.origin !== window.location.origin) return;
            if (event.data?.type !== "preview-location-changed") return;
            const params = new URLSearchParams(event.data.search);
            params.delete("preview_frame");
            history.replace({ pathname: event.data.pathname, search: `?${params.toString()}` });
        };
        window.addEventListener("message", handler);
        return () => window.removeEventListener("message", handler);
    }, [history]);

    // The wrapper's own real width - i.e. how much horizontal space is
    // actually available in the real, unscaled layout around it - is what
    // decides the shrink factor, independent of previewWidth itself (the
    // CSS width the iframe's own page renders its layout at internally).
    // useLayoutEffect, not useEffect, so the very first paint already has
    // the right scale instead of a one-frame flash at scale:1.
    useLayoutEffect(() => {
        const el = wrapperRef.current;
        if (!el) return;
        const update = () => setScale(el.clientWidth / previewWidth);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [previewWidth]);

    const safetyTimersRef = useRef([]);
    useEffect(() => () => {
        contentObserverRef.current?.disconnect();
        if (measureTimerRef.current) clearTimeout(measureTimerRef.current);
        safetyTimersRef.current.forEach(clearTimeout);
    }, []);

    // Same-origin iframe (own site, own route) - contentDocument is reachable
    // directly, no postMessage handshake needed for measuring height. Fires
    // only on a real `src` navigation (a new/first tournament) - tab
    // switches no longer touch `src` at all, see the postMessage effect
    // above.
    const handleLoad = () => {
        setIsLoaded(true);
        const doc = iframeRef.current?.contentDocument;
        if (!doc) return;
        // index.css sets `body { min-height: 100vh }` - inside this iframe,
        // "100vh" resolves against whatever THIS iframe's own CSS height
        // currently is (the very thing this function computes and sets).
        // Once a tall tab has pushed that height up, switching to a
        // shorter tab can never measure smaller than that stale height
        // without first shrinking the iframe back down here - the framed
        // body's own min-height re-inflates scrollHeight right back up to
        // match it. Resetting to near-zero immediately before reading
        // scrollHeight (both synchronous, so nothing paints in between)
        // breaks that feedback loop and gets the real, current height.
        const iframeEl = iframeRef.current;
        const measure = () => {
            // Deferred a tick, not run synchronously inside the
            // ResizeObserver callback below: changing iframeEl's own height
            // here changes what "100vh" resolves to *inside* the framed
            // document (see the comment above), which is exactly what that
            // same observer is watching (doc.documentElement) - mutating it
            // from within its own notification cycle is the textbook
            // trigger for the browser's "ResizeObserver loop completed with
            // undelivered notifications" warning. Deferring lets the current
            // notification cycle finish first.
            //
            // setTimeout, not requestAnimationFrame (tried first): rAF only
            // ever fires around a real paint, which a backgrounded/hidden
            // tab (minimized, switched away from, or - the case that
            // actually surfaced this - driven by devtools/automation with
            // document.hidden true) can throttle indefinitely. That left
            // this permanently stuck: contentHeight never left its initial
            // 0 (iframe pinned to a bare "100vh" fallback, wrapper pinned
            // to its 200px floor), with the framed page's own real content -
            // and its BottomSheetPanel - rendering far past both and
            // getting clipped by the wrapper's overflow:hidden, reading as
            // "the sheet doesn't exist". setTimeout(fn, 0) isn't tied to
            // painting, so it still fires while hidden.
            //
            // If already scheduled, do nothing - do NOT cancel and
            // reschedule. Cancelling and restarting on every call sounds
            // like reasonable debouncing but is actually a bug: mutating
            // iframeEl's height below reliably triggers *another*
            // ResizeObserver notification before the deferred callback
            // gets to run, so every scheduled one kept getting cancelled by
            // the next notification before it ever fired. Letting an
            // already-scheduled one run means it always executes at least
            // once per burst, using whatever scrollHeight is current
            // *then* - stale by a tick at worst, never stuck.
            if (measureTimerRef.current) return;
            measureTimerRef.current = setTimeout(() => {
                measureTimerRef.current = null;
                if (iframeEl) iframeEl.style.height = "0px";
                const real = doc.documentElement.scrollHeight;
                // Restored directly here, not left to React's own next
                // render: if `real` happens to equal the current
                // contentHeight state (the common case - this same
                // ResizeObserver callback re-fires for the settled,
                // unchanged size after its own first invocation), React
                // bails out of re-applying an unchanged style prop, which
                // would otherwise leave this iframe permanently collapsed
                // at the "0px" set two lines up.
                if (iframeEl) iframeEl.style.height = `${real}px`;
                setContentHeight(real);
            });
        };
        remeasureRef.current = measure;
        measure();
        contentObserverRef.current?.disconnect();
        const ro = new ResizeObserver(measure);
        ro.observe(doc.documentElement);
        contentObserverRef.current = ro;

        // Safety net on top of the ResizeObserver above: the tab's own data
        // fetch (groups/draws/schedule, each their own API call) can add
        // content well after this initial load event and after the
        // ResizeObserver's own burst has already settled on an earlier,
        // shorter snapshot (e.g. an empty/loading state) - a legitimate
        // *further* growth some time later doesn't always land as a new
        // notification if it lands while measure()'s own height-reset
        // briefly zeroes things out. Re-measuring a few more times over the
        // next few seconds catches whatever the observer's burst-debounce
        // (the `if (measureTimerRef.current) return` above) missed, without
        // needing to reason precisely about which async fetch finishes when.
        safetyTimersRef.current.forEach(clearTimeout);
        safetyTimersRef.current = [500, 1500, 3000].map(delay => setTimeout(measure, delay));
    };

    return (
        // backgroundColor on both the wrapper and the iframe itself (not
        // just relying on the framed document's own body background) -
        // every (re)mount here is a real navigation (new src, see the
        // comment above onLoad), and until that document has loaded far
        // enough to paint its own body, an iframe with no background of its
        // own shows through as plain white. Matching the app's current
        // theme here means that gap reads as "still loading" instead of a
        // jarring white flash, dark theme included.
        // Floor is a small fixed number (not window.innerHeight - that was
        // tried, to smooth the height jump once contentHeight arrives, but
        // it inflates this wrapper past the actual scaled content on any
        // tab shorter than the real viewport, e.g. Information. The framed
        // page's own BottomSheetPanel is `position: fixed` relative to the
        // iframe's own internal viewport, so it pins to the bottom of the
        // real content - not this wrapper's artificially tall box - which
        // read as a huge empty gap between the peeking sheet and the real
        // page below it. The transition still smooths the height change on
        // its own, just without permanently over-sizing this box.
        // 0.35s + an ease-out-quint curve (fast start, long gentle tail),
        // not the plain 0.2s ease-out this used to be - each individual
        // jump (initial 200px floor -> real height, then every safety-net
        // re-measure at 500/1500/3000ms as async tab data lands) still
        // happens as its own discrete step, but stretching how long each
        // one takes to settle reads as the box smoothly unrolling instead
        // of visibly snapping to the next size.
        <div ref={wrapperRef} style={{ width: "100%", overflow: "hidden", height: Math.max(contentHeight * scale, 200), backgroundColor: "var(--body-background)", transition: "height 0.35s cubic-bezier(0.16, 1, 0.3, 1)" }}>
            <iframe
                ref={iframeRef}
                title="Tournament preview"
                src={src}
                onLoad={handleLoad}
                scrolling="no"
                style={{
                    width: previewWidth,
                    height: contentHeight || "100vh",
                    border: "none",
                    overflow: "hidden",
                    transform: `scale(${scale})`,
                    transformOrigin: "top left",
                    display: "block",
                    backgroundColor: "var(--body-background)",
                }}
            />
        </div>
    );
};

export default TournamentPreviewFrame;
